import { AuthContext } from 'contexts/AuthContext';
import { addDays } from 'date-fns';
import IApiError from 'global/interfaces/api';
import { TrackingType } from 'global/interfaces/reward';
import { useContext, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { sendReferral } from 'services/rewardService';
import { showError } from 'utils/errorHandler';

interface ReferalLocalEntity {
  referredByUserId: string;
  expiry: string;
}

export default function useRewardTracking() {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const authContext = useContext(AuthContext);

  async function handleReferral(trackingType: TrackingType): Promise<void> {
    if (!searchParams.has('referralId') && trackingType === TrackingType.Click) {
      return;
    }

    const existingRefStr: string | null = localStorage.getItem('referral');
    const existingRefObj: ReferalLocalEntity | null = existingRefStr ? JSON.parse(existingRefStr) : null;
    const refId: string = searchParams.get('referralId') ?? existingRefObj?.referredByUserId ?? '';

    if (refId === '') {
      throw new Error('Referrer id not found');
    }

    if (authContext.user?.id === refId) {
      return;
    }

    const referralExpiry: Date = existingRefObj?.expiry ? new Date(existingRefObj.expiry) : new Date();
    const isExpired = new Date() > referralExpiry;

    if (isExpired) {
      removeReferral();
      return;
    }

    try {
      setLoading(true);
      await sendReferral({ referredByUserId: refId, trackingType });
      const referralExpiry = addDays(new Date(), 30);

      const newReferral: ReferalLocalEntity = {
        referredByUserId: refId,
        expiry: referralExpiry.toDateString(),
      };

      localStorage.setItem('referral', JSON.stringify(newReferral));
    } catch (error: unknown) {
      showError(error as IApiError);
    }
  }

  return { handleReferral, referralLoading: loading };
}

function removeReferral() {
  localStorage.removeItem('referral');
}
